import {
  Box,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { motion } from "framer-motion";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { UnitType } from "src/store/api/generatedApi";
import { useTranslation } from "react-i18next";

interface MaterialsGroupHeaderProps {
  availableSummaries: Record<string, UnitType | null>;
  selectedSummaryName: string | null;
  onSummaryChange: (summaryName: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  canCollapse: boolean;
}

export const MaterialsGroupHeader = ({
  availableSummaries,
  selectedSummaryName,
  onSummaryChange,
  isCollapsed,
  onToggleCollapse,
  canCollapse,
}: MaterialsGroupHeaderProps) => {
  const { t } = useTranslation();
  const { t: tenantT } = useTenantTranslation();
  const formatUnits = useUnitsFormatter(true);
  const theme = useTheme();

  const handleChange = (event: SelectChangeEvent) => {
    onSummaryChange(event.target.value);
  };

  const summaryEntries = Object.entries(availableSummaries).sort(
    ([aName], [bName]) => aName.localeCompare(bName)
  );
  const showDropdown = summaryEntries.length > 1;

  const selectedEntry = summaryEntries.find(
    ([name]) => name === selectedSummaryName
  );
  const selectedDisplayText = selectedEntry
    ? selectedEntry[1] === null
      ? t(selectedEntry[0])
      : `${t(selectedEntry[0])}${formatUnits(selectedEntry[1])}`
    : "";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        height: "100%",
        px: 1,
        py: 0.5,
      }}
    >
      {showDropdown ? (
        <Typography
          variant="body2"
          role="heading"
          aria-level={2}
          aria-label={`${tenantT("materials")} selection group`}
          sx={{ fontWeight: "fontWeightSemiBold", whiteSpace: "nowrap" }}
        >
          {tenantT("materials")}
        </Typography>
      ) : null}

      {showDropdown ? (
        <Tooltip title={selectedDisplayText} placement="top" arrow>
          <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
          >
            <Select
              size="small"
              value={selectedSummaryName ?? ""}
              onChange={handleChange}
              variant="standard"
              sx={{
                minWidth: 100,
                fontSize: "0.75rem",
                "& .MuiSelect-select": {
                  py: 0.25,
                  fontSize: "0.75rem",
                },
                "& .MuiInput-underline:before": {
                  borderBottom: `1px solid ${theme.palette.divider}`,
                },
                "& .MuiInput-underline:hover:before": {
                  borderBottom: `1px solid ${theme.palette.divider}`,
                },
              }}
            >
              {summaryEntries.map(([summaryName, unitType]) => (
                <MenuItem
                  key={summaryName}
                  value={summaryName}
                  sx={{ fontSize: "0.75rem" }}
                >
                  {unitType === null
                    ? t(summaryName)
                    : `${t(summaryName)}${formatUnits(unitType)}`}
                </MenuItem>
              ))}
            </Select>
          </motion.div>
        </Tooltip>
      ) : summaryEntries.length == 1 && summaryEntries[0] ? (
        <SummaryHeader
          summaryName={summaryEntries[0][0]}
          summaryUnitType={summaryEntries[0][1]}
        />
      ) : null}

      {canCollapse ? (
        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
          <IconButton
            size="small"
            onClick={onToggleCollapse}
            sx={{
              p: 0.25,
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "action.hover",
              },
            }}
          >
            {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </motion.div>
      ) : null}
    </Box>
  );
};

const SummaryHeader = ({
  summaryName,
  summaryUnitType,
}: {
  summaryName: string;
  summaryUnitType: UnitType | null;
}) => {
  const { t } = useTranslation();
  const formatUnits = useUnitsFormatter(true);

  const displayText =
    summaryUnitType === null
      ? t(summaryName)
      : `${t(summaryName)}${formatUnits(summaryUnitType)}`;

  return (
    <Tooltip title={displayText} placement="top" arrow>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: "100%",
        }}
      >
        {displayText}
      </Typography>
    </Tooltip>
  );
};
